import { memo, useState, useRef, useCallback } from 'react';

import { useEventContext } from '../../app/provider/useEventContext';
import helpIcon from '../../assets/icons/contact-us/Circle.svg';
import { FLOATING_HELP_BUTTON_TEXTS as TEXTS } from '../constants/floatingHelpButton.constants';
import useClickOutside from '../hooks/useClickOutside';

const ensureAbsoluteUrl = (url: string): string => {
  if (!url || url === '#') return url;
  if (/^https?:\/\//i.test(url) || url.startsWith('//')) return url;
  return `https://${url}`;
};

const FloatingHelpButton = memo(function FloatingHelpButton() {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { eventData } = useEventContext();

  const toggleOpen = useCallback(() => setIsOpen((prev) => !prev), []);
  const close = useCallback(() => setIsOpen(false), []);

  useClickOutside(containerRef, close, isOpen);

  const faqUrl = eventData?.faqUrl?.trim();
  const showFaq = eventData?.faqStatus === 'enable' && Boolean(faqUrl);
  const contactUrl = ensureAbsoluteUrl(eventData?.customerServiceContact || '#');
  const absoluteFaqUrl = faqUrl ? ensureAbsoluteUrl(faqUrl) : '#';

  return (
    <div
      ref={containerRef}
      className="fixed bottom-[20px] right-[20px] z-[9999]"
    >
      {isOpen && (
        <div
          className="absolute bottom-[50px] right-0 w-[300px] flex flex-col items-start p-[20px] gap-[10px] rounded-lg bg-[#F2F9FF] shadow-[0px_3px_6px_-2px_rgba(12,17,29,0.12)]"
          role="dialog"
          aria-label={TEXTS.ARIA_DIALOG}
        >
          <h3 className="m-0 w-[220px] font-[Noto_Sans] text-[14px] font-semibold leading-[20px] text-[#002B66]">
            {TEXTS.HEADING}
          </h3>

          <p className="m-0 self-stretch font-[Noto_Sans] text-[12px] font-normal leading-[18px] text-[#002B66]">
            {TEXTS.BODY_PREFIX}{' '}
            <a
              href={contactUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              {TEXTS.CONTACT_LINK_LABEL}
            </a>
            {showFaq ? (
              <>
                {' '}{TEXTS.FAQ_CONJUNCTION}{' '}
                <a
                  href={absoluteFaqUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  {TEXTS.FAQ_LINK_LABEL}
                </a>
                .
              </>
            ) : (
              '.'
            )}
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={toggleOpen}
        className="flex items-center justify-center p-[10px] rounded-full bg-[#002B66] shadow-[0px_3px_6px_-2px_rgba(12,17,29,0.12)] focus:outline-none focus-visible:ring-[2px] focus-visible:ring-[#002B66]"
        aria-label={isOpen ? TEXTS.ARIA_CLOSE : TEXTS.ARIA_OPEN}
        aria-expanded={isOpen}
      >
        <div className="relative w-[20px] h-[20px] flex items-center justify-center">
          <img
            src={helpIcon}
            alt=""
            aria-hidden="true"
            className={`absolute inset-0 w-full h-full transition-[opacity,transform] duration-300 ${
              isOpen
                ? 'opacity-0 rotate-[-90deg]'
                : 'opacity-100 rotate-0'
            }`}
          />
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            className={`absolute inset-0 w-full h-full transition-[opacity,transform] duration-300 ${
              isOpen
                ? 'opacity-100 rotate-45'
                : 'opacity-0 rotate-0'
            }`}
          >
            <path
              d="M12 5V19M5 12H19"
              stroke="#F2F9FF"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </button>
    </div>
  );
});

export default FloatingHelpButton;